import React, { useMemo } from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useIntl } from "react-intl";
import { useSocialChatCore } from "../../../../cores/socialChat";
import { useMeCore } from "../../../../cores/me";
import {
  WHATSAPP_COLOR,
  FACEBOOK_COLOR,
  INSTAGRAM_COLOR,
  LINKED_IN_COLOR,
  LINKED_IN_BUSINESS_COLOR,
} from "../../../../constants/socialColors.constant";
import {
  ConversationGroup,
  Conversations,
} from "../../../../cores/socialChat/interfaces/conversations-group.interface";
import { InputWembii } from "../../InputWembii";
import SelectContactStateDropdown from "../SelectContactState/ContactStateDropdownOptions";
import { Conversation } from "../../../../cores/socialChat/interfaces/conversations.interface";
import { SocialType } from "../../../../cores/socialChat/types/social.types";
import "./ChatFilterModal.scss";

const channelIconColor = (
  enabled: boolean,
  isSelected: boolean,
  brandColor: string
) => {
  if (!enabled) return "#dee2e6";
  return isSelected ? brandColor : "#adb5bd";
};

const ChatFilterModal: React.FC<{ conversations: Conversations }> = ({
  conversations,
}) => {
  const { socialSettings } = useMeCore();
  const {
    conversationStatus,
    selectedSocial,
    setSelectedSocial,
    setConversationStatus,
    setDateFilter,
    dateFilter,
  } = useSocialChatCore();
  const intl = useIntl();

  const configuredSocials = useMemo(
    () => ({
      whatsapp: !!socialSettings?.whatsapp,
      instagram: !!socialSettings?.instagramMensseger,
      facebook: !!socialSettings?.facebook,
      linkedin: !!socialSettings?.linkedIn,
      linkedinBusiness: !!socialSettings?.linkedInBusiness,
    }),
    [socialSettings]
  );

  const isValidDate = (date: Date) => {
    return date instanceof Date && !isNaN(date.getMilliseconds());
  };

  const filterDateConversation = (conversation: Conversation) => {
    const fromDate = new Date(dateFilter.from);
    const toDate = new Date(dateFilter.to);
    const conversationDate = new Date(conversation.timestamp.full);

    if (isValidDate(fromDate) || isValidDate(toDate)) {
      if (!isValidDate(fromDate) && isValidDate(toDate)) {
        return conversationDate <= toDate;
      }
      if (isValidDate(fromDate) && !isValidDate(toDate)) {
        return conversationDate >= fromDate;
      }
      if (isValidDate(fromDate) && isValidDate(toDate)) {
        return conversationDate >= fromDate && conversationDate <= toDate;
      }
    }
    return true;
  };

  const getSocialCounters = () => {
    const empty: ConversationGroup = {
      active: [],
      archived: [],
      inTrash: [],
    };
    const {
      all = empty,
      facebook = empty,
      instagram = empty,
      web = empty,
      whatsapp = empty,
      linkedin = empty,
      linkedinBusiness = empty,
    } = conversations;

    return {
      instagram:
        instagram.active.filter(filterDateConversation).length +
        instagram.archived.filter(filterDateConversation).length +
        instagram.inTrash.filter(filterDateConversation).length,

      facebook:
        facebook.active.filter(filterDateConversation).length +
        facebook.archived.filter(filterDateConversation).length +
        facebook.inTrash.filter(filterDateConversation).length,
      linkedin:
        linkedin.active.filter(filterDateConversation).length +
        linkedin.archived.filter(filterDateConversation).length +
        linkedin.inTrash.filter(filterDateConversation).length,
      linkedinBusiness:
        linkedinBusiness.active.filter(filterDateConversation).length +
        linkedinBusiness.archived.filter(filterDateConversation).length +
        linkedinBusiness.inTrash.filter(filterDateConversation).length,
      whatsapp:
        whatsapp.active.filter(filterDateConversation).length +
        whatsapp.archived.filter(filterDateConversation).length +
        whatsapp.inTrash.filter(filterDateConversation).length,
      web:
        web.active.filter(filterDateConversation).length +
        web.archived.filter(filterDateConversation).length +
        web.inTrash.filter(filterDateConversation).length,
      all:
        all.active.filter(filterDateConversation).length +
        all.archived.filter(filterDateConversation).length +
        all.inTrash.filter(filterDateConversation).length,
    };
  };

  const counters = getSocialCounters();

  const getStatusesCounter = () => {
    const bucket = conversations[selectedSocial] ?? {
      active: [],
      archived: [],
      inTrash: [],
    };
    return {
      active: bucket.active.filter(filterDateConversation).length,
      archived: bucket.archived.filter(filterDateConversation).length,
      inTrash: bucket.inTrash.filter(filterDateConversation).length,
    };
  };

  const statusCounts = getStatusesCounter();

  const channels = useMemo(
    () =>
      [
        {
          id: "all" as SocialType,
          label: intl.formatMessage({ id: "app.chat.filterLabel" }),
          icon: ["fas", "message"] as IconProp,
          brand: "#2563eb",
          enabled: true,
        },
        {
          id: "web",
          label: intl.formatMessage({ id: "app.chat.channel.web" }),
          icon: ["fas", "globe"] as IconProp,
          brand: "#0d4b7e",
          enabled: true,
        },
        {
          id: "whatsapp",
          label: intl.formatMessage({ id: "app.contacts.whatsapp" }),
          icon: ["fab", "whatsapp"] as IconProp,
          brand: WHATSAPP_COLOR,
          enabled: configuredSocials.whatsapp,
        },
        {
          id: "facebook",
          label: intl.formatMessage({ id: "app.sidebar.metrics.Facebook" }),
          icon: ["fab", "facebook"] as IconProp,
          brand: FACEBOOK_COLOR,
          enabled: configuredSocials.facebook,
        },
        {
          id: "instagram",
          label: intl.formatMessage({ id: "app.sidebar.metrics.Instagram" }),
          icon: ["fab", "instagram"] as IconProp,
          brand: INSTAGRAM_COLOR,
          enabled: configuredSocials.instagram,
        },
        {
          id: "linkedin",
          label: intl.formatMessage({ id: "app.chat.channel.linkedin" }),
          icon: ["fab", "linkedin"] as IconProp,
          brand: LINKED_IN_COLOR,
          enabled: configuredSocials.linkedin,
        },
        {
          id: "linkedinBusiness",
          label: intl.formatMessage({
            id: "app.chat.channel.linkedinBusiness",
          }),
          icon: ["fab", "linkedin"] as IconProp,
          brand: LINKED_IN_BUSINESS_COLOR,
          enabled: configuredSocials.linkedinBusiness,
        },
      ] as const,
    [intl, configuredSocials]
  );

  return (
    <div className="chat-filter-modal">
      <header className="chat-filter-modal__head">
        <h2 className="chat-filter-modal__title">
          {intl.formatMessage({ id: "app.crmBusiness.filters" })}
        </h2>
        <p className="chat-filter-modal__subtitle">
          {intl.formatMessage({ id: "app.chat.filterModal.subtitle" })}
        </p>
      </header>

      <div className="chat-filter-modal__card chat-filter-modal__card--flush">
        <span className="chat-filter-modal__section-label">
          {intl.formatMessage({ id: "app.chat.filterModal.sectionContact" })}
        </span>
        <SelectContactStateDropdown className="chat-filter-modal__contact-dropdown w-100 justify-content-between" />
      </div>

      <div className="chat-filter-modal__card">
        <span className="chat-filter-modal__section-label">
          {intl.formatMessage({ id: "app.chat.filterModal.sectionStatus" })}
        </span>
        <div
          className="chat-filter-modal__pills"
          role="group"
          aria-label={intl.formatMessage({
            id: "app.chat.filterModal.sectionStatus",
          })}
        >
          <button
            type="button"
            className={cn("chat-filter-modal__pill", {
              "chat-filter-modal__pill--active":
                conversationStatus === "active",
            })}
            onClick={() => setConversationStatus("active")}
          >
            <span className="chat-filter-modal__pill-icon">
              <FontAwesomeIcon
                icon={["fas", "comments"]}
                size="sm"
                color={
                  conversationStatus === "active" ? "#0284c7" : "#94a3b8"
                }
              />
            </span>
            <span>{intl.formatMessage({ id: "app.chat.active" })}</span>
            <span className="chat-filter-modal__pill-count">
              ({statusCounts.active})
            </span>
          </button>
          <button
            type="button"
            className={cn("chat-filter-modal__pill", {
              "chat-filter-modal__pill--active":
                conversationStatus === "archived",
            })}
            onClick={() => setConversationStatus("archived")}
          >
            <span className="chat-filter-modal__pill-icon">
              <FontAwesomeIcon
                icon={["fas", "comments"]}
                size="sm"
                color={
                  conversationStatus === "archived" ? "#0284c7" : "#94a3b8"
                }
              />
            </span>
            <span>{intl.formatMessage({ id: "app.chat.archived" })}</span>
            <span className="chat-filter-modal__pill-count">
              ({statusCounts.archived})
            </span>
          </button>
          <button
            type="button"
            className={cn("chat-filter-modal__pill", {
              "chat-filter-modal__pill--active":
                conversationStatus === "inTrash",
            })}
            onClick={() => setConversationStatus("inTrash")}
          >
            <span className="chat-filter-modal__pill-icon">
              <FontAwesomeIcon
                icon={["fas", "trash"]}
                size="sm"
                color={
                  conversationStatus === "inTrash" ? "#0284c7" : "#94a3b8"
                }
              />
            </span>
            <span>{intl.formatMessage({ id: "app.chat.inTrash" })}</span>
            <span className="chat-filter-modal__pill-count">
              ({statusCounts.inTrash})
            </span>
          </button>
        </div>
      </div>

      <div className="chat-filter-modal__card">
        <span className="chat-filter-modal__section-label">
          {intl.formatMessage({ id: "app.chat.filterModal.sectionChannel" })}
        </span>
        <div className="chat-filter-modal__grid">
          {channels.map((ch) => {
            const count = counters[ch.id as keyof typeof counters] ?? 0;
            const selected = selectedSocial === ch.id;
            const iconColor = channelIconColor(ch.enabled, selected, ch.brand);
            return (
              <button
                key={ch.id}
                type="button"
                disabled={!ch.enabled}
                title={
                  !ch.enabled
                    ? intl.formatMessage({
                        id: "app.chat.filterModal.channelDisabled",
                      })
                    : undefined
                }
                className={cn("chat-filter-modal__channel", {
                  "chat-filter-modal__channel--selected": selected,
                  "chat-filter-modal__channel--disabled": !ch.enabled,
                })}
                onClick={() => ch.enabled && setSelectedSocial(ch.id)}
              >
                <span className="chat-filter-modal__channel-icon">
                  <FontAwesomeIcon
                    icon={ch.icon}
                    size="lg"
                    color={iconColor}
                  />
                </span>
                <span className="chat-filter-modal__channel-text">{ch.label}</span>
                <span className="chat-filter-modal__channel-count">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="chat-filter-modal__card">
        <span className="chat-filter-modal__section-label">
          {intl.formatMessage({ id: "app.chat.filterModal.sectionDates" })}
        </span>
        <div className="chat-filter-modal__dates">
          <div className="chat-filter-modal__date-field">
            <InputWembii
              mb={false}
              onChange={(value) => setDateFilter(value, "from")}
              type="date"
              max={
                isValidDate(new Date(dateFilter.to))
                  ? new Date(dateFilter.to).toISOString().split("T")[0]
                  : undefined
              }
              className="w-100"
              label={intl.formatMessage({ id: "app.socialManager.from" })}
            />
          </div>
          <div className="chat-filter-modal__date-field">
            <InputWembii
              mb={false}
              type="date"
              min={
                isValidDate(new Date(dateFilter.from))
                  ? new Date(dateFilter.from).toISOString().split("T")[0]
                  : undefined
              }
              className="w-100"
              label={intl.formatMessage({ id: "app.socialManager.to" })}
              onChange={(value) => setDateFilter(value, "to")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatFilterModal;
